import React, { useEffect } from 'react';
import { FiSquare, FiCircle, FiTriangle, FiStar, FiHexagon, FiHeart, FiArrowRight, FiMinus, FiPenTool } from 'react-icons/fi';
import useEditorStore from '../../../stores/useEditorStore';
import { ColorPicker, SliderInput } from '../../common';
import { STROKE_STYLES } from '../../../constants/editorConstants';
import { panelStyle, titleStyle, sectionStyle, labelStyle } from '../../../styles/panelStyles';
import { fromFabricValue, toFabricValue } from '../../../utils/gradientUtils';
import { createShapeObject } from '../../../utils/shapeUtils';

// ── 커스텀 SVG 아이콘 ──

const RoundedRectIcon = ({ size = 24, color = '#6b7280' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
  </svg>
);

const DiamondIcon = ({ size = 24, color = '#6b7280' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12,2 22,12 12,22 2,12" />
  </svg>
);

const CrossIcon = ({ size = 24, color = '#6b7280' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="8,2 16,2 16,8 22,8 22,16 16,16 16,22 8,22 8,16 2,16 2,8 8,8" />
  </svg>
);

// ── 도형 목록 ──

const SHAPES = [
  { id: 'rect', icon: FiSquare, label: '사각형' },
  { id: 'roundedRect', icon: RoundedRectIcon, label: '둥근사각형' },
  { id: 'circle', icon: FiCircle, label: '원' },
  { id: 'triangle', icon: FiTriangle, label: '삼각형' },
  { id: 'star', icon: FiStar, label: '별' },
  { id: 'diamond', icon: DiamondIcon, label: '마름모' },
  { id: 'pentagon', icon: FiPenTool, label: '오각형' },
  { id: 'hexagon', icon: FiHexagon, label: '육각형' },
  { id: 'arrow', icon: FiArrowRight, label: '화살표' },
  { id: 'cross', icon: CrossIcon, label: '십자가' },
  { id: 'heart', icon: FiHeart, label: '하트' },
  { id: 'line', icon: FiMinus, label: '선' },
];

const isShapePath = (obj) => obj.type === 'path';
const SHAPE_TYPES = ['rect', 'circle', 'ellipse', 'triangle', 'polygon', 'line'];
const DEFAULT_SIZE = 300;
const STROKE_PROPS = { paintFirst: 'stroke', strokeUniform: true };

const ShapeSettingsPanel = () => {
  const { canvas, canvasSize, selectedObjects, shapeSettings, setShapeSettings, pushHistory, syncLayersFromCanvas } = useEditorStore();

  const selectedShapeObject = selectedObjects.find(obj =>
    SHAPE_TYPES.includes(obj.type) || isShapePath(obj)
  );

  useEffect(() => {
    if (selectedShapeObject) {
      const objType = isShapePath(selectedShapeObject) ? 'path' : selectedShapeObject.type;
      const w = selectedShapeObject.width || 100;
      const h = selectedShapeObject.height || 100;
      handleSettingChange({
        type: objType === 'ellipse' ? 'circle' : objType,
        fill: fromFabricValue(selectedShapeObject.fill, w, h) || '#3b82f6',
        stroke: fromFabricValue(selectedShapeObject.stroke, w, h) || '#1e40af',
        strokeWidth: selectedShapeObject.strokeWidth || 2,
        strokeDashArray: selectedShapeObject.strokeDashArray || null,
        opacity: selectedShapeObject.opacity ?? 1,
        rx: selectedShapeObject.rx || 0,
        ry: selectedShapeObject.ry || 0,
      });
    }
  }, [selectedShapeObject?.id]);

  const addShapeToCanvas = (type) => {
    if (!canvas) return;
    const { fill, stroke, strokeWidth, strokeDashArray, opacity, rx, ry } = shapeSettings;
    const size = DEFAULT_SIZE;
    const cx = (canvasSize.width - size) / 2;
    const cy = (canvasSize.height - size) / 2;
    const fabricFill = toFabricValue(fill, size, size);
    const fabricStroke = toFabricValue(stroke, size, size);
    const common = { originX: 'left', originY: 'top', stroke: fabricStroke, strokeWidth, strokeDashArray, opacity, ...STROKE_PROPS };

    const obj = createShapeObject(type, common, { cx, cy, size, fabricFill, fabricStroke, rx, ry });
    if (obj) {
      canvas.add(obj);
      canvas.setActiveObject(obj);
      canvas.renderAll();
      syncLayersFromCanvas();
      pushHistory();
    }
  };

  const handleShapeTypeClick = (type) => {
    setShapeSettings({ type });
    addShapeToCanvas(type);
  };

  const handleSettingChange = (newSettings) => {
    setShapeSettings(newSettings);

    if (selectedShapeObject && canvas && !newSettings.type) {
      const fabricSettings = { ...newSettings };
      const w = selectedShapeObject.width || 100;
      const h = selectedShapeObject.height || 100;
      if (newSettings.fill !== undefined) {
        fabricSettings.fill = toFabricValue(newSettings.fill, w, h);
      }
      if (newSettings.stroke !== undefined) {
        fabricSettings.stroke = toFabricValue(newSettings.stroke, w, h);
      }

      if (newSettings.strokeWidth !== undefined) {
        // strokeWidth 변경 시 중심점 보정 (바운딩박스 변화로 인한 위치 이동 방지)
        const center = selectedShapeObject.getCenterPoint();
        selectedShapeObject.set({ ...fabricSettings, ...STROKE_PROPS });
        selectedShapeObject.setCoords();
        const newCenter = selectedShapeObject.getCenterPoint();
        selectedShapeObject.set({
          left: selectedShapeObject.left + (center.x - newCenter.x),
          top: selectedShapeObject.top + (center.y - newCenter.y),
        });
        selectedShapeObject.setCoords();
      } else {
        selectedShapeObject.set({ ...fabricSettings, ...STROKE_PROPS });
      }
      canvas.renderAll();
      pushHistory();
    }
  };

  return (
    <div style={panelStyle}>
      <h3 style={titleStyle}>{selectedShapeObject ? '도형 설정' : '도형 추가'}</h3>

      {!selectedShapeObject && (
        <div style={{ ...sectionStyle, marginTop: '20px' }}>
          <label style={{ ...labelStyle, fontSize: '15px' }}>도형 종류</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
            {SHAPES.map((shape) => {
              const isActive = shapeSettings.type === shape.id;
              return (
                <button
                  key={shape.id}
                  onClick={() => handleShapeTypeClick(shape.id)}
                  style={{
                    padding: '12px 4px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    border: isActive ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: isActive ? '#eff6ff' : '#ffffff',
                    cursor: 'pointer',
                  }}
                >
                  <shape.icon size={24} color={isActive ? '#3b82f6' : '#6b7280'} />
                  <span style={{ fontSize: '11px', color: isActive ? '#3b82f6' : '#6b7280' }}>
                    {shape.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ ...sectionStyle, marginTop: selectedShapeObject ? '20px' : undefined }}>
        <ColorPicker
          label="채우기 색상"
          value={shapeSettings.fill}
          onChange={(fill) => handleSettingChange({ fill })}
          showTransparent
          size="large"
        />
      </div>

      <div style={sectionStyle}>
        <ColorPicker
          label="테두리 색상"
          value={shapeSettings.stroke}
          onChange={(stroke) => handleSettingChange({ stroke })}
          showTransparent
          size="large"
        />
      </div>

      <SliderInput
        label="테두리 두께"
        value={shapeSettings.strokeWidth}
        onChange={(strokeWidth) => handleSettingChange({ strokeWidth })}
        min={0}
        max={20}
        unit="px"
      />

      <div style={sectionStyle}>
        <label style={labelStyle}>테두리 스타일</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {STROKE_STYLES.map((style) => (
            <button
              key={style.id}
              onClick={() => handleSettingChange({ strokeDashArray: style.dashArray })}
              style={{
                flex: 1,
                padding: '8px',
                border: (shapeSettings.strokeDashArray === style.dashArray) ||
                  (!shapeSettings.strokeDashArray && style.id === 'solid')
                  ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              {style.label}
            </button>
          ))}
        </div>
      </div>

      <SliderInput
        label="투명도"
        value={Math.round((shapeSettings.opacity ?? 1) * 100)}
        onChange={(val) => handleSettingChange({ opacity: val / 100 })}
        min={0}
        max={100}
        unit="%"
      />

      {(shapeSettings.type === 'rect' || shapeSettings.type === 'roundedRect') && (
        <SliderInput
          label="모서리 둥글기"
          value={shapeSettings.rx || 0}
          onChange={(rx) => handleSettingChange({ rx, ry: rx })}
          min={0}
          max={50}
          unit="px"
        />
      )}
    </div>
  );
};

export default ShapeSettingsPanel;
